/**
 * Centralized API Configuration
 * 
 * All backend and frontend port configurations should reference this file.
 * This eliminates hardcoded localhost URLs throughout the codebase.
 * 
 * Configuration Priority:
 * 1. Runtime window variables (set in public/index.html)
 * 2. Default values for local development
 * 
 * Note: Build-time environment variables (VITE_*) are not used in production builds
 * to avoid bundle issues. Use window.__BACKEND_ORIGIN__ for runtime configuration.
 */

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

function isPrivateNetworkHost(host: string): boolean {
  return /^(localhost|127\.0\.0\.1|10\.|192\.168\.|172\.(1[6-9]|2\d|3[0-1])\.)/.test(host);
}

function normalizeBackendOrigin(origin: string): string {
  try {
    const url = new URL(origin);
    if (!url.port && isPrivateNetworkHost(url.hostname)) {
      url.port = '5001';
    }
    return url.origin;
  } catch {
    return origin;
  }
}

/**
 * Get backend port from various sources
 */
function getBackendPort(): string {
  if (isBrowser) {
    // Check runtime window variable
    const windowBackendOrigin = (window as any).__BACKEND_ORIGIN__;
    if (windowBackendOrigin) {
      const match = windowBackendOrigin.match(/:(\d+)/);
      if (match) return match[1];
    }
  }
  
  // Default to 5000
  return '5000';
}

/**
 * Get frontend port from various sources
 */
function getFrontendPort(): string {
  // Default to 8000
  return '8000';
}

/**
 * Get backend host (defaults to localhost)
 */
function getBackendHost(): string {
  if (isBrowser) {
    const windowBackendOrigin = (window as any).__BACKEND_ORIGIN__;
    if (windowBackendOrigin) {
      try {
        const url = new URL(windowBackendOrigin);
        return url.hostname;
      } catch {
        // Invalid URL, fall through to default
      }
    }
  }
  
  return 'localhost';
}

/**
 * Centralized API Configuration Object
 */
export const API_CONFIG = {
  get BACKEND_HOST() {
    return getBackendHost();
  },
  get BACKEND_PORT() {
    return getBackendPort();
  },
  get FRONTEND_PORT() {
    return getFrontendPort();
  },
  
  /**
   * Get full backend origin URL (e.g., http://www.rjbd.org:5000)
   * ALWAYS reads from window.__BACKEND_ORIGIN__ at runtime
   */
  getBackendOrigin(): string {
    if (isBrowser) {
      const windowOrigin = (window as any).__BACKEND_ORIGIN__;
      if (windowOrigin) {
        const normalizedOrigin = normalizeBackendOrigin(windowOrigin);
        console.log('[API_CONFIG] Using window.__BACKEND_ORIGIN__:', normalizedOrigin);
        return normalizedOrigin;
      }
      console.warn('[API_CONFIG] window.__BACKEND_ORIGIN__ not set! Using fallback.');
    }
    // Fallback only for SSR or if window variable not set (should never happen in production)
    const fallback = `http://${this.BACKEND_HOST}:${this.BACKEND_PORT}`;
    console.warn('[API_CONFIG] Fallback origin:', fallback);
    return fallback;
  },
  
  /**
   * Get API base URL (e.g., http://localhost:5000/api)
   */
  getApiBaseUrl(): string {
    if (isBrowser && (window as any).__API_BASE__) {
      return (window as any).__API_BASE__;
    }
    return `${this.getBackendOrigin()}/api`;
  },
  
  /**
   * Get full media URL from relative path
   * @param path - Relative path starting with / (e.g., /Media/Photos/user123/photo.jpg)
   */
  getMediaUrl(path: string): string {
    if (!path) return '';
    
    // If already a full URL, return as-is
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    
    // Ensure path starts with /
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    
    const origin = this.getBackendOrigin();
    const fullUrl = `${origin}${normalizedPath}`;
    console.log('[API_CONFIG] getMediaUrl:', path, '→', fullUrl);
    return fullUrl;
  },
  
  /**
   * Get full API endpoint URL
   * @param endpoint - API endpoint path (e.g., 'users' or '/users')
   */
  getApiEndpoint(endpoint: string): string {
    // Remove leading slash if present
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    return `${this.getApiBaseUrl()}/${normalizedEndpoint}`;
  }
};

/**
 * Export individual helper functions for convenience
 */
export const getBackendOrigin = () => API_CONFIG.getBackendOrigin();
export const getApiBaseUrl = () => API_CONFIG.getApiBaseUrl();
export const getMediaUrl = (path: string) => API_CONFIG.getMediaUrl(path);
export const getApiEndpoint = (endpoint: string) => API_CONFIG.getApiEndpoint(endpoint);

/**
 * Default export for easy importing
 */
export default API_CONFIG;
